import React, {
  createContext,
  useContext,
  useCallback,
  useState,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
// import { useGroupFringeQuery } from 'utils/api/queries';

import {
  useBudgetMutation,
  usePostDuplicateCategory,
  usePostDuplicateCategoryLineItem,
  useTabsMutuation,
  useFringeMutation,
  useGroupMutation,
} from 'utils/api/mutations';
import {
  useBudgetQueries,
  useTabsQueries,
  useFringeQuery,
  useGroupFringeQuery,
} from 'utils/api/queries';
import { useAppContext } from 'contexts/AppContext';
import ALL_ROUTES from 'config/routes';
import {
  getSelectedTabFromCache,
  // setOpenTabsInCache,
  setSelectedTabInCache,
  userTokenExists,
} from 'utils/common';
import { useDispatch } from 'react-redux';
import { updateTotal } from 'store/slices/budgetSessionSlice';
import API_ENDPOINTS from 'utils/api/endpoints';
import { downloadBudgetCsv } from 'utils/budgetCsvExport';
import {
  createTimestampedPdfFileName,
  downloadFile,
  getLocalTimezone,
  toastStyle,
} from 'utils/helper';
import {
  BUDGET_VIEW_STATE_QUERY_PARAM,
  decodeBudgetViewStateFromUrl,
} from 'utils/budgetDeepLinks';
import {
  getBudgetTemplateName,
  isAicpTemplateName,
  PRINT_PRODUCTION_DETAILS_DEFAULT_TEMPLATE_NAMES,
} from 'utils/constants';
import { getInitialPrintPaperSize } from 'utils/printPaperSizes';
import {
  addBudgetToCachedList,
  buildOptimisticDeletedBudgetState,
  buildOptimisticCreatedBudgetTabs,
  hasServerBackedCreatedBudgetTab,
  removeBudgetFromCachedList,
  removeBudgetFromCachedTabs,
} from 'utils/budgetTabTransitions';
import {
  prefetchMyBudgets,
  prefetchOpenBudgetWorkspaces,
} from 'utils/budgetWorkspaceCache';

const homeScreenContext = createContext({});
export { homeScreenContext as HomeScreenContext };

const scheduleIdleTask = callback => {
  if (typeof window.requestIdleCallback === 'function') {
    const idleCallbackId = window.requestIdleCallback(callback, {
      timeout: 1000,
    });
    return () => window.cancelIdleCallback?.(idleCallbackId);
  }

  const timeoutId = window.setTimeout(callback, 0);
  return () => window.clearTimeout(timeoutId);
};

const getAccountNumbersViewStorageKey = budgetId =>
  budgetId ? `cinebudget-show-account-numbers:${budgetId}` : null;

const getBudgetSidePanelStorageKey = budgetId =>
  budgetId ? `cinebudget-budget-side-panel-open:${budgetId}` : null;

const getViewStateDebugEnabled = () =>
  typeof window !== 'undefined' &&
  (window.localStorage?.getItem('CBP_DEBUG_VIEW_STATE') === '1' ||
    (process.env.NODE_ENV === 'development' &&
      window.localStorage?.getItem('CBP_DEBUG_VIEW_STATE') !== '0'));

const logViewStateDebug = (...args) => {
  if (getViewStateDebugEnabled()) {
    // eslint-disable-next-line no-console
    console.debug(
      '[CBP view state]',
      ...args.map(arg =>
        typeof arg === 'string' ? arg : JSON.stringify(arg, null, 2)
      )
    );
  }
};

const hasSavedTabViewState = tab =>
  (Array.isArray(tab?.view_state?.hierarchy) &&
    tab.view_state.hierarchy.length) ||
  Boolean(tab?.view_state?.panel?.open);

const getViewStateSavedAt = viewState => {
  const timestamp = Date.parse(viewState?.savedAt || viewState?.saved_at || '');
  return Number.isFinite(timestamp) ? timestamp : 0;
};

const summarizeTabForViewStateDebug = tab => ({
  id: tab?.id,
  budget: tab?.budget,
  name: tab?.name,
  isActive: tab?.is_active,
  hasViewState: Boolean(hasSavedTabViewState(tab)),
  savedAt: tab?.view_state?.savedAt || tab?.view_state?.saved_at || null,
  hierarchy: tab?.view_state?.hierarchy?.map(
    item => `${item?.type}:${item?.id}`
  ),
  detail: tab?.view_state?.detail,
  cell: tab?.view_state?.cell,
});

const mergeTabsWithLocalViewState = (incomingTabs, localTabSources = []) => {
  const normalizedTabs = Array.isArray(incomingTabs) ? incomingTabs : [];
  const localTabs = localTabSources
    .flat()
    .filter(Boolean)
    .filter(hasSavedTabViewState);
  const localByKey = new Map();

  localTabs.forEach(tab => {
    if (tab?.id) localByKey.set(`id:${tab.id}`, tab);
    if (tab?.budget) localByKey.set(`budget:${tab.budget}`, tab);
  });

  return normalizedTabs.map(tab => {
    const localTab =
      localByKey.get(`id:${tab?.id}`) ||
      localByKey.get(`budget:${tab?.budget}`);

    if (!localTab?.view_state) return tab;

    const incomingHasState = hasSavedTabViewState(tab);
    const incomingSavedAt = getViewStateSavedAt(tab?.view_state);
    const localSavedAt = getViewStateSavedAt(localTab.view_state);
    const shouldUseLocal =
      !incomingHasState || (localSavedAt && localSavedAt > incomingSavedAt);

    return shouldUseLocal ? { ...tab, view_state: localTab.view_state } : tab;
  });
};

const normalizeOpenTabsForDisplay = tabs => {
  const normalizedTabs = Array.isArray(tabs) ? tabs : [];
  const seenBudgetIds = new Set();

  return normalizedTabs.filter(tab => {
    const key = tab?.budget ?? tab?.id;

    if (key === null || key === undefined) {
      return true;
    }

    const normalizedKey = key.toString();
    if (seenBudgetIds.has(normalizedKey)) {
      return false;
    }

    seenBudgetIds.add(normalizedKey);
    return true;
  });
};

export const HomeScreenContextProvider = ({ children }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isUserLoggedIn, userData, permissions, handleLogout } =
    useAppContext();
  const [selectedBudget, setSelectedBudget] = useState();
  const [enteringBudgetId, setEnteringBudgetId] = useState(null);
  const [openingCreatedBudgetId, setOpeningCreatedBudgetId] = useState(null);
  const enteringBudgetTimeoutRef = useRef(null);
  const [currentBudgetViewState, setCurrentBudgetViewState] = useState(null);
  const [deepLinkViewStateByBudget, setDeepLinkViewStateByBudget] = useState(
    {}
  );
  const [activeSwimlane, setActiveSwimlane] = useState({});
  const [swimlanes, setSwimlanes] = useState([]);
  const [projectTotal, setProjectTotal] = useState(0);
  const [addedContextualSuggestions, setAddedContextualSuggestions] = useState(
    {}
  );
  const queryClient = useQueryClient();
  const createTabsMutation = useTabsMutuation.useCreateTabMutation();
  const makeAciveTabMutation = useTabsMutuation.useMakeActiveTabMutation();
  const reorderTabsMutation = useTabsMutuation.useReorderTabsMutation();
  const closeTabsMutation = useTabsMutuation.useCloseTabMutation();
  const createBudgetMutation = useBudgetMutation.useCreateBudgetMutation();
  const moveBudgetMutation = useBudgetMutation.useMoveBudgetMutation();
  const moveBudgetFolderMutation =
    useBudgetMutation.useMoveBudgetFolderMutation();
  const createFolderMutation = useBudgetMutation.useCreateFolderMutation();
  const renameFolderMutation = useBudgetMutation.useRenameFolderMutuation();
  const duplicateBudgetMutation =
    useBudgetMutation.useDuplicateBudgetMutation();
  const deleteBudgetMutation = useBudgetMutation.useDeleteBudgetMutation();
  // friges
  const deleteFringeMutation = useFringeMutation.useDeleteFringeMutation();
  const deleteGroupMutation = useGroupMutation.useDeleteGroupMutation();
  const updateFrindeMutation = useFringeMutation.useUpdateFringeMutation();
  const UpdateGroupsMutation = useGroupMutation.useUpdateGroupMutation();
  const [frindgeDetailsForModal, setFringeDetailsForModal] = useState();
  const [groupDetailsForModal, setGroupDetailsForModal] = useState();
  const createSwimlaneMutation = useBudgetMutation.useCreateSwimlaneMutation();
  const addSubCategoryMutation = useBudgetMutation.useAddSubcategoryMutation();
  const updateSwimlaneMutation = useBudgetMutation.useUpdateSwimlaneMutation();
  const deleteSwimlaneMutation = useBudgetMutation.useDeleteSwimlaneMutation();
  const [showCreateGroupsModal, setshowCreateGroupsModal] = useState(false);
  const createSwimlaneDetailsMutation =
    useBudgetMutation.useCreateSwimlaneDetailsMutation();
  const updateSwimlaneDetailsMutation =
    useBudgetMutation.useUpdateSwimlaneDetailsMutation();
  const deleteSwimlaneDetailsMutation =
    useBudgetMutation.useDeleteSwimlaneDetailsMutation();
  const shareBudgetMutation = useBudgetMutation.useShareBudgetMutation();
  const updatePublicBudgetLinkMutation =
    useBudgetMutation.useUpdatePublicBudgetLinkMutation();
  const removeBudgetAccessMutation =
    useBudgetMutation.useRemoveBudgetAccessMutation();
  const budgetDownloadMutation = useBudgetMutation.useBudgetDownloadMutation();
  const removeMultiBudgetsMutuation =
    useBudgetMutation.useRemoveMultiBudgetsMutuation();
  const removeFolderMutuation = useBudgetMutation.useRemoveFolderMutuation();
  const createDuplicateCategory =
    usePostDuplicateCategory.usePostDuplicateCategory();
  const createDuplicateCategoryLineItem =
    usePostDuplicateCategoryLineItem.usePostDuplicateCategoryLineItem();
  const [showDetailedView, setShowDetailedViewState] = useState(false);
  const [showCreateBudget, setShowCreateBudget] = useState(false);
  const [showScriptImport, setShowScriptImport] = useState(false);
  const [showBudgetImport, setShowBudgetImport] = useState(false);
  const [showEditBudget, setShowEditBudget] = useState(false);
  const [, setShowUpdateFringe] = useState(false);
  const [showCreateFolder, setShowCreateFolder] = useState(false);
  const [showDeleteBudget, setShowDeleteBudget] = useState(false);
  const [activeBudget, setActiveBudget] = useState(0);

  //fringe
  const [showDeleteFringe, setShowDeleteFringe] = useState(false);
  const [showEditFringeGroup, setShowEditFringeGroup] = useState(false);
  const [showDeleteFringeGroup, setShowDeleteFringeGroup] = useState(false);

  const [showDeleteGroup, setShowDeleteGroup] = useState(false);
  const [, setShowUpdateGroup] = useState(false);
  const [showcolumnfringe, setShowColumnFringe] = useState(false);
  const [accountNumbersViewPreference, setAccountNumbersViewPreference] =
    useState({
      budgetId: null,
      value: false,
    });
  const showAccountNumbers = accountNumbersViewPreference.value;
  const setShowAccountNumbers = useCallback(
    updater => {
      setAccountNumbersViewPreference(prevPreference => ({
        budgetId: selectedBudget?.budget ?? prevPreference.budgetId ?? null,
        value:
          typeof updater === 'function'
            ? updater(prevPreference.value)
            : Boolean(updater),
      }));
    },
    [selectedBudget?.budget]
  );
  const [aiInsightsViewPreference, setAiInsightsViewPreference] = useState({
    budgetId: null,
    value: false,
  });
  const showAiInsights =
    aiInsightsViewPreference.budgetId === (selectedBudget?.budget ?? null) &&
    aiInsightsViewPreference.value;
  const setShowAiInsights = useCallback(
    updater => {
      setAiInsightsViewPreference(prevPreference => ({
        budgetId: selectedBudget?.budget ?? prevPreference.budgetId ?? null,
        value:
          typeof updater === 'function'
            ? updater(prevPreference.value)
            : Boolean(updater),
      }));
    },
    [selectedBudget?.budget]
  );
  const setShowDetailedView = useCallback(updater => {
    setShowDetailedViewState(current => {
      const next = typeof updater === 'function' ? updater(current) : updater;
      const isCurrentDetailOpen = Boolean(current?.show);
      const isNextDetailOpen = Boolean(next?.show);
      const isSameOpenDetail =
        isCurrentDetailOpen === isNextDetailOpen &&
        (!isNextDetailOpen ||
          (current?.level === next?.level &&
            current?.selectedItem?.id === next?.selectedItem?.id));

      return isSameOpenDetail ? current : next;
    });
  }, []);

  const [showDeleteMultiBudgets, setShowDeleteMultiBudgets] = useState(false);
  const [showDeleteFolder, setShowDeleteFolder] = useState(false);
  const [renameBudget, setRenameBudget] = useState();
  const [delData, setdelData] = useState();
  const [folderData, setFolderData] = useState();
  const [showShareBudget, setShowShareBudget] = useState(false);
  const [showBudgetSettings, setShowBudgetSettings] = useState(false);
  const [showSaveAsTemplate, setShowSaveAsTemplate] = useState(false);
  const [showCostReport, setShowCostReport] = useState(false);
  const [showLogOutModal, setShowLogoutModal] = useState(false);
  const [showNewBudgetId, setshowNewBudgetId] = useState(false);
  const [printShow, setPrintShow] = useState(false);
  const [keyStore, setKeyStore] = useState('');
  const [tabActive, setTabActive] = useState(null);
  const [mutationCompleted, setMutationCompleted] = useState(false);
  const [fringeApplied, sefFringeAppliedTotal] = useState(0);
  const hideCreateBudgetModal = () => setShowCreateBudget(false);
  const showCreateBudgetModal = () => setShowCreateBudget(true);
  const hideScriptImportModal = () => setShowScriptImport(false);
  const showScriptImportModal = () => setShowScriptImport(true);
  const hideBudgetImportModal = () => setShowBudgetImport(false);
  const showBudgetImportModal = () => setShowBudgetImport(true);

  const hideEditBudgetModal = () => setShowEditBudget(false);
  const showEditBudgetModal = () => setShowEditBudget(true);

  const hideUpdateFringeModal = () => setShowUpdateFringe(false);
  const showUpdateFringeModal = () => setShowUpdateFringe(true);

  const hideUpdateGroupModal = () => setShowUpdateGroup(false);
  const showUpdateGroupModal = () => setShowUpdateGroup(true);

  const hideCreateFolderModal = () => setShowCreateFolder(false);
  const showCreateFolderModal = () => setShowCreateFolder(true);
  const hideDeleteBudgetModal = () => setShowDeleteBudget(false);
  const showDeleteBudgetModal = () => setShowDeleteBudget(true);
  const showDeleteMultiBudgetModal = () => setShowDeleteMultiBudgets(true);
  const hideDeleteMultiBudgetModal = () => setShowDeleteMultiBudgets(false);
  // fringe
  const hideDeleteFringeModal = () => setShowDeleteFringe(false);
  const showDeleteFringeModal = () => setShowDeleteFringe(true);

  const hideEditFringeGroupModal = () => setShowEditFringeGroup(false);
  const showEditFringeGroupModal = () => setShowEditFringeGroup(true);

  const hideDeleteFringeGroupModal = () => setShowDeleteFringeGroup(false);
  const showDeleteFringeGroupModal = () => setShowDeleteFringeGroup(true);

  const hideDeleteGroupModal = () => setShowDeleteGroup(false);
  const showDeleteGroupModal = () => setShowDeleteGroup(true);

  const showDeleteFolderModal = () => setShowDeleteFolder(true);
  const hideDeleteFolderModal = () => setShowDeleteFolder(false);

  const setBudgetToRename = () => {
    setRenameBudget(budgetData);
    showCreateBudgetModal();
  };
  const setBudgetToEdit = () => {
    setRenameBudget(budgetData);
    showEditBudgetModal();
  };
  const setNewBudgetIdFunc = flag => {
    setshowNewBudgetId(flag);
  };

  const setSelectDelGroup = async selData => {
    setdelData(selData);
    const groupIds = selData.map(item => item.id);
    const payload = { Ids: groupIds };
    const response = await API_ENDPOINTS.groups.delete_group_info(payload);
    setGroupDetailsForModal(response.data);

    showDeleteGroupModal();
  };

  const setSeletedDelFringe = async selData => {
    setdelData(selData);
    const fringeIds = selData.map(item => item.id);
    const payload = { Ids: fringeIds };
    const response = await API_ENDPOINTS.fringes.delete_fringe_info(payload);
    setFringeDetailsForModal(response.data);
    showDeleteFringeModal();
  };

  const setSelectFringeGroup = selData => {
    setdelData(selData);
    showEditFringeGroupModal();
  };

  const setSelectDelFringeGroup = selData => {
    setdelData(selData);
    showDeleteFringeGroupModal();
  };

  const setSelectedDeldata = selData => {
    setdelData(selData);
    showDeleteMultiBudgetModal();
  };
  const setFolderSelectedDeldata = selData => {
    setFolderData(selData);
    showDeleteFolderModal();
  };
  const setWindowReload = () => {
    window?.location?.reload();
  };
  const clearBudgetRename = () => setRenameBudget(null);
  const showShareBudgetModal = useCallback(() => setShowShareBudget(true), []);
  const hideShareBudgetModal = () => setShowShareBudget(false);
  const showBudgetSettingsModal = () => setShowBudgetSettings(true);
  const hideBudgetSettingsModal = () => setShowBudgetSettings(false);
  const showSaveAsTemplateModal = () => setShowSaveAsTemplate(true);
  const hideSaveAsTemplateModal = () => setShowSaveAsTemplate(false);
  const showCostReportModal = () => setShowCostReport(true);
  const hideCostReportModal = () => setShowCostReport(false);
  const hideLogOutModal = () => setShowLogoutModal(false);
  const logOutModalShow = () => setShowLogoutModal(true);

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const instanceType = queryParams.get('type') || 'budget';

  const { refetch: refetchGroup } =
    useGroupFringeQuery.useGroupFringeDetailsQuery({
      enabled: Boolean(isUserLoggedIn && activeBudget),
      filters: {
        instance_type: instanceType,
        active_id: activeBudget,
      },
    });

  // fringe
  const { data: fringeListData, refetch: refetchFringe } =
    useFringeQuery.useFringeDetailsQuery({
      enabled: Boolean(isUserLoggedIn && activeBudget),
      filters: {
        instance_type: instanceType,
        active_id: activeBudget,
      },
    });
  const fringeList = fringeListData?.data;

  const {
    status: getBudgetsStatus,
    data: budgetListDataa,
    isFetching: isFetchingBudgets,
    isError: isErrorBudgets,
    refetch: refetchBudgets,
  } = useBudgetQueries.useBudgetQuery({
    enabled: Boolean(isUserLoggedIn),
  });
  const budgetList = budgetListDataa?.data;
  const [budgetListData, setbudgetListData] = useState([]);
  //fetch  open tabs
  const {
    status: getTabsStatus,
    data: tabsData,
    isFetching: isFetchingTabs,
    isError: isErrorTabs,
    refetch: refetchTabs,
  } = useTabsQueries.useOpenTabsQuery({
    enabled: Boolean(isUserLoggedIn),
  });
  const openTabsListResponse = tabsData?.data;
  const [openTabsList, setOpenTabsList] = useState(openTabsListResponse);
  const [openTabsListInitial, setOpenTabsListInitial] =
    useState(openTabsListResponse);
  const viewStateTabSourcesRef = useRef([]);
  const handledBudgetQueryRef = useRef('');
  const [getbudgetDropdownData, setBudgetDropdownData] = useState([]);
  const shownTabs = 8;

  useEffect(() => {
    if (!isUserLoggedIn) return undefined;

    return scheduleIdleTask(() => {
      prefetchMyBudgets(queryClient).catch(() => undefined);
    });
  }, [isUserLoggedIn, queryClient]);

  useEffect(() => {
    if (!isUserLoggedIn || !Array.isArray(openTabsListResponse)) {
      return undefined;
    }

    const budgetIds = [...openTabsListResponse]
      .sort((left, right) => Number(left?.is_active) - Number(right?.is_active))
      .map(tab => tab?.budget)
      .filter(Boolean);

    return scheduleIdleTask(() => {
      prefetchOpenBudgetWorkspaces(queryClient, budgetIds).catch(
        () => undefined
      );
    });
  }, [isUserLoggedIn, openTabsListResponse, queryClient]);

  //fringe data like budget data

  const { refetch: refetchFringeId } = useFringeQuery.useFringeDetailsQuery({
    enabled: false,
    filters: {
      instance_type: instanceType,
      active_id: activeBudget,
    },
  });
  const {
    status: getBudgetIdStatus,
    data: budgetIdData,
    isFetching: isFetchingBudgetId,
    isError: isErrorBudgetId,
    refetch: refetchBudgetId,
  } = useBudgetQueries.useBudgetByIdQuery({
    enabled: Boolean(isUserLoggedIn && selectedBudget?.budget),
    budgetId: selectedBudget?.budget,
  });
  const budgetData = budgetIdData?.data;

  // Sync projectTotal from backend budgetData.total_cost (single source of truth)
  // and keep per-budget session data in Redux
  useEffect(() => {
    const budgetId = selectedBudget?.budget;
    const rawTotal = budgetData?.total_cost;

    if (rawTotal !== undefined && budgetId) {
      const numericTotal = parseFloat(rawTotal) || 0;
      setProjectTotal(numericTotal);
      // Update per-budget session tracking
      dispatch(
        updateTotal({
          budgetId,
          newTotal: numericTotal,
        })
      );
    } else if (!budgetId) {
      // Reset to 0 when no budget is selected
      setProjectTotal(0);
    }
  }, [
    budgetData?.total_cost,
    selectedBudget?.budget,
    setProjectTotal,
    dispatch,
  ]);

  const {
    status: getBudgetTypeStatus,
    data: budgetTypesData,
    isFetching: isFetchingBudgetTypes,
    isError: isErrorBudgetTypes,
    refetch: refetchBudgetTypes,
  } = useBudgetQueries.useBudgetTypeQuery({
    enabled: Boolean(isUserLoggedIn),
  });
  const budgetTypes = budgetTypesData?.data;

  const {
    status: getFolderType,
    data: foldersData,
    isFetching: isFetchingFolders,
    isError: isErrorFoldersTypes,
    refetch: refetchFolders,
  } = useBudgetQueries.useFolderBudgetQuery({
    enabled: Boolean(isUserLoggedIn),
  });
  const foldersList = foldersData?.data;
  const [folderListing, setFolderListing] = useState(foldersList);
  const [queryParameters] = useSearchParams();
  const queryBudgetId = queryParameters.get('budget');
  const queryViewStateParam = queryParameters.get(
    BUDGET_VIEW_STATE_QUERY_PARAM
  );

  const rememberDeepLinkViewState = useCallback((budgetId, viewState) => {
    if (!budgetId || !viewState) return;

    setDeepLinkViewStateByBudget(current => ({
      ...current,
      [budgetId.toString()]: viewState,
    }));
  }, []);

  const clearDeepLinkViewState = useCallback(budgetId => {
    if (!budgetId) return;

    setDeepLinkViewStateByBudget(current => {
      const budgetKey = budgetId.toString();
      if (!Object.prototype.hasOwnProperty.call(current, budgetKey)) {
        return current;
      }

      const next = { ...current };
      delete next[budgetKey];
      return next;
    });
  }, []);

  const handleCreateTabs = useCallback(
    async (budget_id, { deepLinkViewState = null } = {}) => {
      if (deepLinkViewState) {
        rememberDeepLinkViewState(budget_id, deepLinkViewState);
      }

      const payload = [];
      payload?.push({
        budget: budget_id,
      });
      try {
        const response = await createTabsMutation.mutateAsync(payload);
        await makeAciveTabMutation.mutateAsync(response?.data?.[0]?.id);
        await refetchTabs();
        toast.dismiss();
        return response;
      } catch (error) {
        console.error('Error creating budget tab:', error);
        return null;
      }
    },
    [
      createTabsMutation,
      makeAciveTabMutation,
      refetchTabs,
      rememberDeepLinkViewState,
    ]
  );

  useEffect(() => {
    if (queryBudgetId !== null && queryBudgetId !== undefined) {
      const queryKey = `${queryBudgetId}:${queryViewStateParam || ''}`;
      if (handledBudgetQueryRef.current === queryKey) return;

      handledBudgetQueryRef.current = queryKey;
      const deepLinkViewState = decodeBudgetViewStateFromUrl(
        queryViewStateParam,
        queryBudgetId
      );
      handleCreateTabs(queryBudgetId, {
        deepLinkViewState,
      });
      if (deepLinkViewState?.notificationId) {
        API_ENDPOINTS.budgets
          .mark_notification_read(deepLinkViewState.notificationId)
          .then(() =>
            queryClient.invalidateQueries({
              queryKey: ['budget-notifications'],
            })
          )
          .catch(() => null);
      }
    } else {
      handledBudgetQueryRef.current = '';
    }
  }, [handleCreateTabs, queryBudgetId, queryClient, queryViewStateParam]);
  const {
    status: getContextualSuggestionsStatus,
    data: contextualSuggestionsData,
    isFetching: isFetchingContextualSuggestions,
    isError: isErrorContextualSuggestions,
    refetch: refetchContextualSuggestions,
  } = useBudgetQueries.useContextualSuggestionsQuery({
    enabled: Boolean(isUserLoggedIn),
  });
  const contextualSuggestions = contextualSuggestionsData?.data;

  const {
    status: getCollaboratorsStatus,
    data: collaboratorsData,
    isFetching: isFetchingCollaborators,
    isError: isErrorCollaborators,
    refetch: refetchCollaborators,
  } = useBudgetQueries.useCollaboratorsQuery({
    enabled: Boolean(isUserLoggedIn && selectedBudget?.budget),
    budgetId: selectedBudget?.budget,
  });
  const collaboratorsList = collaboratorsData?.data;

  const {
    status: getBudgetPermissionsStatus,
    data: budgetPermissionData,
    isFetching: isFetchingBudgetPermissions,
    isError: isErrorBudgetPermissions,
    refetch: refetchBudgetPermissions,
  } = useBudgetQueries.useBudgetPermissionsQuery({
    enabled: Boolean(isUserLoggedIn && selectedBudget?.budget),
    budgetId: selectedBudget?.budget,
  });
  const budgetPermissions = budgetPermissionData?.data?.[0];

  const { pathname } = useLocation();

  useEffect(() => {
    if (!userTokenExists()) {
      navigate(ALL_ROUTES.AUTH_CHILDREN.LOGIN);
      handleLogout(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, navigate]);

  useEffect(() => {
    viewStateTabSourcesRef.current = [
      openTabsList,
      openTabsListInitial,
      selectedBudget,
    ];
  }, [openTabsList, openTabsListInitial, selectedBudget]);

  useEffect(
    () => () => {
      if (enteringBudgetTimeoutRef.current) {
        window.clearTimeout(enteringBudgetTimeoutRef.current);
      }
    },
    []
  );

  useEffect(() => {
    if (!openingCreatedBudgetId || !Array.isArray(tabsData?.data)) return;

    if (
      hasServerBackedCreatedBudgetTab(tabsData.data, openingCreatedBudgetId)
    ) {
      setOpeningCreatedBudgetId(null);
    }
  }, [openingCreatedBudgetId, tabsData]);

  useEffect(() => {
    if (!tabsData?.data || openingCreatedBudgetId) return;

    const hydratedTabs = normalizeOpenTabsForDisplay(
      mergeTabsWithLocalViewState(tabsData.data, viewStateTabSourcesRef.current)
    );

    setOpenTabsListInitial(hydratedTabs);

    logViewStateDebug('tabs query refreshed initial list', {
      tabs: hydratedTabs.map(summarizeTabForViewStateDebug),
    });
  }, [openingCreatedBudgetId, tabsData]);

  useEffect(() => {
    setFolderListing(foldersData?.data);
  }, [foldersData]);

  useEffect(() => {
    if (tabsData && tabsData?.data && !openingCreatedBudgetId) {
      const hydratedTabs = normalizeOpenTabsForDisplay(
        mergeTabsWithLocalViewState(
          tabsData.data,
          viewStateTabSourcesRef.current
        )
      );
      let openTabs = [];
      let budgetListDataVar = [];
      const budgetDrop = [];
      if (hydratedTabs?.length > shownTabs) {
        openTabs = hydratedTabs.slice(0, shownTabs);
        budgetListDataVar = hydratedTabs.slice(shownTabs, hydratedTabs.length);
      } else {
        openTabs = hydratedTabs;
      }
      for (let i = 0; i < budgetListDataVar?.length; i++) {
        budgetDrop?.push({
          label: budgetListDataVar[i]?.name,
          value: budgetListDataVar[i]?.budget,
        });
      }
      setbudgetListData(budgetDrop);
      setBudgetDropdownData(budgetDrop);
      setOpenTabsList(openTabs);
      const activeTab = openTabs.find(tab => tab?.is_active) || openTabs[0];
      setSelectedBudget(activeTab);
      setActiveBudget(activeTab?.id);

      logViewStateDebug('tabs query applied visible list', {
        activeTab: summarizeTabForViewStateDebug(activeTab),
        visibleTabs: openTabs.map(summarizeTabForViewStateDebug),
      });
    }
  }, [openingCreatedBudgetId, tabsData]);

  useEffect(() => {
    if (!selectedBudget) {
      const selectedTab = getSelectedTabFromCache();
      setSelectedBudget(selectedTab || budgetList?.[0]);
    }
  }, [budgetList, setSelectedBudget, selectedBudget]);

  useEffect(() => {
    selectedBudget && setSelectedTabInCache(selectedBudget);
  }, [selectedBudget]);

  const isBudgetOwner = useCallback(
    budget => {
      const selected = budget?.id ? budget : selectedBudget;
      return selected?.created_by_email === userData?.email;
    },
    [selectedBudget, userData]
  );

  const hasEditAccess = useCallback(() => {
    return (
      isBudgetOwner() ||
      budgetPermissions?.permissions?.some(permission => {
        return (
          permissions?.find(
            perm => permission?.toString() === perm?.value?.toString()
          )?.label === 'editor'
        );
      })
    );
  }, [budgetPermissions, permissions, isBudgetOwner]);

  const hasBudgetEditAccessInList = useCallback(budgetPermission => {
    return budgetPermission === 'editor';
  }, []);

  const duplicateBudget = async withValues => {
    toast.loading(
      `Please wait while we are duplicating budget ${selectedBudget?.name} ...`
    );
    try {
      const response = await duplicateBudgetMutation.mutateAsync({
        budget_id: selectedBudget?.budget,
        withValues,
      });
      const newBudget = { ...response?.data?.data, duplicate: true };
      if (!newBudget?.id) {
        throw new Error('Duplicate response did not include a budget ID');
      }

      refetchBudgets?.();
      refetchBudgetId?.();

      let tabResponse = await handleCreateTabs(newBudget.id);
      if (!tabResponse) {
        tabResponse = await handleCreateTabs(newBudget.id);
      }

      toast.dismiss();
      if (!tabResponse) {
        toast.error(
          'Budget duplicated, but its tab could not be opened. You can open it from My Budgets.'
        );
      }
    } catch (error) {
      console.error('Error duplicating budget:', error);
      toast.dismiss();
      toast.error('Something went wrong while duplicating the budget');
    }
  };

  const duplicateBudgetWithValues = () => duplicateBudget(true);

  const changeTab = useCallback(
    (tab, callActive = false) => {
      const latestTab =
        [...(openTabsList || []), ...(openTabsListInitial || [])].find(
          openTab =>
            openTab?.id?.toString() === tab?.id?.toString() ||
            openTab?.budget?.toString() === tab?.budget?.toString()
        ) || tab;

      logViewStateDebug('change tab requested', {
        requestedTab: summarizeTabForViewStateDebug(tab),
        latestTab: summarizeTabForViewStateDebug(latestTab),
        currentTab: summarizeTabForViewStateDebug(selectedBudget),
        callActive,
      });

      if (
        latestTab?.budget?.toString() !== selectedBudget?.budget?.toString()
      ) {
        setShowDetailedView({ show: false });
      }

      setSelectedBudget(latestTab);

      if (callActive && !latestTab?.duplicate && !latestTab?.is_optimistic) {
        makeAciveTabMutation
          .mutateAsync(latestTab?.id)
          .then(response => {
            const refreshedTabs = normalizeOpenTabsForDisplay(
              mergeTabsWithLocalViewState(
                Array.isArray(response?.data) ? response.data : [],
                [openTabsList, openTabsListInitial, selectedBudget, latestTab]
              )
            );
            const refreshedTab = refreshedTabs.find(
              openTab => openTab?.id?.toString() === latestTab?.id?.toString()
            );

            if (!refreshedTabs.length) return;

            setOpenTabsListInitial(refreshedTabs);
            setOpenTabsList(refreshedTabs.slice(0, shownTabs));
            setbudgetListData(
              refreshedTabs.slice(shownTabs).map(data => ({
                label: data?.name,
                value: data?.budget,
              }))
            );
            setBudgetDropdownData(
              refreshedTabs.slice(shownTabs).map(data => ({
                label: data?.name,
                value: data?.budget,
              }))
            );

            if (refreshedTab) {
              setSelectedBudget(refreshedTab);
            }

            logViewStateDebug('make active response applied', {
              activeTab: summarizeTabForViewStateDebug(refreshedTab),
              tabs: refreshedTabs.map(summarizeTabForViewStateDebug),
            });
          })
          .catch(() => null);
      }
    },
    [
      makeAciveTabMutation,
      openTabsList,
      openTabsListInitial,
      selectedBudget,
      setShowDetailedView,
    ]
  );

  const renameTab = useCallback(
    (tabId, renamedBudget) => {
      renamedBudget ||= budgetList?.find(
        budget => budget?.id?.toString() === tabId?.toString()
      );
      let openTabs = openTabsList || [];

      openTabs = openTabs.map(tab => {
        if (tab?.budget?.toString() === tabId?.toString()) {
          return {
            ...tab,
            name: renamedBudget?.name,
          };
        }
        return tab;
      });
      setOpenTabsList(openTabs);
    },
    [openTabsList, budgetList]
  );

  const selectTab = useCallback(
    (tabId, selectedTab) => {
      selectedTab ||= budgetList?.find(
        budget => budget?.id?.toString() === tabId?.toString()
      );

      const toOpenTab = tab => {
        if (!tab) return tab;

        return {
          ...tab,
          budget: tab?.budget ?? tab?.id,
        };
      };
      const selectedOpenTab = toOpenTab(selectedTab);
      const selectedBudgetId = selectedOpenTab?.budget?.toString();
      const fullOpenTabs = normalizeOpenTabsForDisplay(
        (openTabsListInitial?.length
          ? openTabsListInitial
          : openTabsList || []
        ).map(toOpenTab)
      );

      let nextTabs = fullOpenTabs;

      if (selectedOpenTab && selectedBudgetId) {
        const existingIndex = fullOpenTabs.findIndex(
          tab => tab?.budget?.toString() === selectedBudgetId
        );

        if (existingIndex >= 0) {
          nextTabs = fullOpenTabs.map((tab, index) =>
            index === existingIndex
              ? {
                  ...tab,
                  ...selectedOpenTab,
                  id: tab?.id,
                  budget: tab?.budget ?? selectedOpenTab?.budget,
                }
              : tab
          );
        } else {
          nextTabs = normalizeOpenTabsForDisplay([
            ...fullOpenTabs,
            selectedOpenTab,
          ]);
        }
      }

      const visibleTabs = nextTabs.slice(0, shownTabs);
      const dropdownTabs = nextTabs.slice(shownTabs);
      const budgetDrop = dropdownTabs.map(data => ({
        label: data?.name,
        value: data?.budget,
      }));

      setOpenTabsListInitial(nextTabs);
      setbudgetListData(budgetDrop);
      setBudgetDropdownData(budgetDrop);
      setOpenTabsList(visibleTabs);
      selectedOpenTab && changeTab(selectedOpenTab);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [openTabsList, openTabsListInitial, budgetList, shownTabs]
  );

  const getTabsDisplayState = useCallback(
    tabs => {
      const normalizedTabs = normalizeOpenTabsForDisplay(tabs);
      const visibleTabs = normalizedTabs.slice(0, shownTabs);
      const dropdownTabs = normalizedTabs.slice(shownTabs);
      const menuItems = dropdownTabs.map(data => ({
        label: data?.name,
        value: data?.budget,
      }));

      return { visibleTabs, menuItems };
    },
    [shownTabs]
  );

  const applyOpenTabsState = useCallback(
    tabs => {
      const normalizedTabs = normalizeOpenTabsForDisplay(tabs);
      const { visibleTabs, menuItems } = getTabsDisplayState(normalizedTabs);

      setOpenTabsListInitial(normalizedTabs);
      setOpenTabsList(visibleTabs);
      setbudgetListData(menuItems);
      setBudgetDropdownData(menuItems);
    },
    [getTabsDisplayState]
  );

  const optimisticallyRemoveBudget = useCallback(
    budgetId => {
      const previousTabs = [
        ...(openTabsListInitial?.length
          ? openTabsListInitial
          : openTabsList || []),
      ];
      const previousSelectedBudget = selectedBudget;
      const previousActiveBudget = activeBudget;
      const previousBudgetListCache = queryClient.getQueryData(['get-budget']);
      const previousOpenTabsCache = queryClient.getQueryData(['get-open-tabs']);
      const nextState = buildOptimisticDeletedBudgetState({
        tabs: previousTabs,
        budgetId,
        selectedTab: selectedBudget,
      });

      queryClient.cancelQueries({ queryKey: ['get-budget'] });
      queryClient.cancelQueries({ queryKey: ['get-open-tabs'] });
      queryClient.setQueryData(['get-budget'], cachedResponse =>
        removeBudgetFromCachedList(cachedResponse, budgetId)
      );
      queryClient.setQueryData(['get-open-tabs'], cachedResponse =>
        removeBudgetFromCachedTabs(cachedResponse, budgetId, selectedBudget)
      );

      applyOpenTabsState(nextState.tabs);
      setSelectedBudget(nextState.selectedTab);
      setActiveBudget(nextState.selectedTab?.id || 0);
      setSelectedTabInCache(nextState.selectedTab || {});

      return () => {
        if (previousBudgetListCache !== undefined) {
          queryClient.setQueryData(['get-budget'], previousBudgetListCache);
        }
        if (previousOpenTabsCache !== undefined) {
          queryClient.setQueryData(['get-open-tabs'], previousOpenTabsCache);
        }

        applyOpenTabsState(previousTabs);
        setSelectedBudget(previousSelectedBudget);
        setActiveBudget(previousActiveBudget);
        setSelectedTabInCache(previousSelectedBudget || {});
      };
    },
    [
      activeBudget,
      applyOpenTabsState,
      openTabsList,
      openTabsListInitial,
      queryClient,
      selectedBudget,
    ]
  );

  const openCreatedBudget = useCallback(
    async budget => {
      const budgetId = budget?.id ?? budget?.budget;
      if (budgetId === null || budgetId === undefined) return null;

      const existingTabs = openTabsListInitial?.length
        ? openTabsListInitial
        : openTabsList || [];
      const optimisticTabs = buildOptimisticCreatedBudgetTabs(
        existingTabs,
        budget
      );

      queryClient.setQueryData(['get-budget'], cachedResponse =>
        addBudgetToCachedList(cachedResponse, budget)
      );
      queryClient.setQueryData(
        ['get-budget-by-id', { budgetId }],
        cachedResponse => cachedResponse || { data: budget }
      );

      setOpeningCreatedBudgetId(budgetId);
      applyOpenTabsState(optimisticTabs);
      setEnteringBudgetId(budgetId);

      if (enteringBudgetTimeoutRef.current) {
        window.clearTimeout(enteringBudgetTimeoutRef.current);
      }
      enteringBudgetTimeoutRef.current = window.setTimeout(() => {
        setEnteringBudgetId(null);
        enteringBudgetTimeoutRef.current = null;
      }, 420);

      try {
        const preloadWorkspace = Promise.allSettled([
          API_ENDPOINTS.budgets.get_budget_by_id(budgetId),
          API_ENDPOINTS.budgets.get_swimlanes(budgetId),
        ]);
        const [response, preloadResults] = await Promise.all([
          createTabsMutation.mutateAsync([
            { budget: budgetId, is_active: true },
          ]),
          preloadWorkspace,
        ]);
        const serverTabs = normalizeOpenTabsForDisplay(response?.data);
        const createdTab = serverTabs.find(
          tab => tab?.budget?.toString() === budgetId.toString()
        );

        if (!createdTab) {
          throw new Error('The created budget tab was not returned.');
        }

        const [budgetDetailsResult, rootCategoriesResult] = preloadResults;
        if (budgetDetailsResult.status === 'fulfilled') {
          queryClient.setQueryData(
            ['get-budget-by-id', { budgetId }],
            budgetDetailsResult.value
          );
        }
        if (rootCategoriesResult.status === 'fulfilled') {
          queryClient.setQueryData(
            ['get-swimlanes-in-budget', { id: budgetId, type: 'swimlane' }],
            rootCategoriesResult.value
          );
        }

        if (serverTabs.length) {
          queryClient.setQueryData(['get-open-tabs'], response);
          applyOpenTabsState(serverTabs);
        }
        setSwimlanes([{ type: 'swimlane', budgetId }]);
        setShowDetailedView({ show: false });
        setSelectedBudget(createdTab);
        setActiveBudget(createdTab.id);

        queryClient
          .invalidateQueries({ queryKey: ['get-budget'] })
          .catch(error =>
            console.error('Error refreshing the budget list:', error)
          );

        return createdTab;
      } catch (error) {
        applyOpenTabsState(existingTabs);
        setOpeningCreatedBudgetId(null);
        console.error('Error opening the created budget tab:', error);
        toast.error(
          'The budget was created, but its tab could not be saved. Refresh to try again.',
          toastStyle
        );
        return null;
      }
    },
    [
      applyOpenTabsState,
      createTabsMutation,
      openTabsList,
      openTabsListInitial,
      queryClient,
      setSwimlanes,
      setShowDetailedView,
    ]
  );

  // Function to handle the tab drag functionality
  // NOTE: Update is done optimistically, first update the frontend then try to make the update
  // query to the backend
  const handleTabDragEnd = useCallback(
    async result => {
      if (openingCreatedBudgetId) return;
      if (!result.destination) return;

      // Do not move it if nothing changes
      if (
        result.destination.droppableId === result.source.droppableId &&
        result.destination.index === result.source.index
      ) {
        return;
      }

      const originalFullList = Array.isArray(openTabsListInitial)
        ? [...openTabsListInitial]
        : [];
      const visibleTabs = Array.isArray(openTabsList) ? openTabsList : [];
      const originalSelectedBudget = selectedBudget;
      const draggedTab = visibleTabs[result.source.index];

      if (!draggedTab?.budget) return;

      const sourceIndex = originalFullList.findIndex(
        tab => tab?.budget?.toString() === draggedTab.budget.toString()
      );
      if (sourceIndex < 0) return;

      const newList = [...originalFullList];
      const [reorderedItem] = newList.splice(sourceIndex, 1);
      newList.splice(result.destination.index, 0, reorderedItem);

      // Perform optimistic update
      applyOpenTabsState(newList);

      // Try to save to backend
      try {
        const newTabList =
          (
            await reorderTabsMutation.mutateAsync({
              budget_ids: newList.map(data => data?.budget),
            })
          )?.data || [];
        const newActiveTab = newTabList.find(
          tab =>
            tab?.budget?.toString() ===
            originalSelectedBudget?.budget?.toString()
        );

        applyOpenTabsState(newTabList);
        if (newActiveTab) {
          setSelectedBudget(newActiveTab);
        }
      } catch (error) {
        // If anything fails, revert all state to the original
        console.error('Failed to save new tab order:', error);
        applyOpenTabsState(originalFullList);
        setSelectedBudget(originalSelectedBudget); // Roll back selected budget too
        //toast.error('Failed to save new tab order.');
      }
    },
    [
      openTabsListInitial,
      openTabsList,
      reorderTabsMutation,
      selectedBudget, // Needed to read the old active budget
      applyOpenTabsState,
      openingCreatedBudgetId,
      setSelectedBudget, // Needed for update and rollback
    ]
  );

  const selectTabFromDropdown = useCallback(
    async tabId => {
      if (openingCreatedBudgetId) return;

      const selectedTabIndex = openTabsListInitial?.findIndex(
        budget => budget?.budget?.toString() === tabId?.toString()
      );

      if (selectedTabIndex < 0) return;

      const newList = [...openTabsListInitial];

      const elementToRemove = newList.splice(selectedTabIndex, 1);
      const firstElement = newList.shift();
      newList.push(firstElement);
      newList.splice(shownTabs - 1, 0, elementToRemove[0]);

      const newTabList =
        (
          await reorderTabsMutation.mutateAsync({
            budget_ids: newList.map(data => data?.budget),
          })
        )?.data || [];
      const newOpenTabListData = [...newTabList];
      const openTabs = newOpenTabListData.slice(0, shownTabs);

      applyOpenTabsState(newOpenTabListData);
      changeTab(openTabs[shownTabs - 1], true);
    },
    [
      openTabsListInitial,
      shownTabs,
      reorderTabsMutation,
      applyOpenTabsState,
      changeTab,
      openingCreatedBudgetId,
    ]
  );

  // const selectTabFromDropdown = useCallback(
  //   (tabId, selectedTab) => {
  //     selectedTab ||= openTabsListInitial?.find(
  //       budget => budget?.id?.toString() === tabId?.toString()
  //     );
  //     let openTabs = openTabsList || [];

  //     if (!openTabs?.length) {
  //       openTabs = [selectedTab];
  //     } else if (
  //       !openTabsList?.find(tab => tab?.id?.toString() === tabId?.toString())
  //     ) {
  //       const startIndex = openTabsList?.length > 3 ? 1 : 0;

  //       // I have changed 3 to 4 in slice
  //       openTabs = [...openTabsList.slice(startIndex, 4), selectedTab];
  //     } else if (selectedTab?.id) {
  //       openTabs = openTabsList.map(tab =>
  //         tab?.id?.toString() === selectedTab?.id?.toString()
  //           ? selectedTab
  //           : tab
  //       );
  //     }
  //     let openTabsTemp = [];
  //     let budgetListDataVar = [];
  //     const budgetDrop = [];
  //     openTabsTemp = openTabs;
  //     budgetListDataVar = tabsData?.data?.filter(
  //       item => !openTabs.some(newItem => newItem?.budget === item?.budget)
  //     );
  //     for (let i = 0; i < budgetListDataVar?.length; i++) {
  //       budgetDrop?.push({
  //         label: budgetListDataVar[i]?.name,
  //         value: budgetListDataVar[i]?.id,
  //       });
  //     }
  //     setbudgetListData(budgetDrop);
  //     setBudgetDropdownData(budgetDrop);
  //     // setOpenTabsListInitial(budgetDrop);
  //     setOpenTabsList(openTabsTemp);
  //     selectedTab && changeTab(selectedTab);
  //   },
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  //   [openTabsList, openTabsListInitial, budgetList]
  // );

  const updateSwimlaneItem = useCallback(
    (payload, onSuccess, onError) =>
      updateSwimlaneMutation.mutate(payload, { onSuccess, onError }),
    [updateSwimlaneMutation]
  );

  /**
   * Invalidate and refetch swimlane queries to get updated costs from backend.
   * The backend is the single source of truth for all costs.
   * This should be called after detail sheets are created/updated/deleted.
   *
   * This function ensures all swimlane-related queries (swimlanes, line-items, line-item-children)
   * are invalidated and refetched, ensuring ALL parent costs update when nested items change,
   * including the top-level swimlanes, grandparents and higher-level parents.
   *
   * @param {Object} options - Optional parameters
   * @param {string|number} options.budgetId - Optional budget ID to explicitly refetch top-level swimlanes
   */
  // Ref to track pending refetch to prevent duplicate calls
  const refetchSwimlaneCostsTimeoutRef = useRef(null);

  const refetchSwimlaneCosts = useCallback(
    (options = {}) => {
      // Clear any pending refetch to debounce multiple calls
      if (refetchSwimlaneCostsTimeoutRef.current) {
        clearTimeout(refetchSwimlaneCostsTimeoutRef.current);
      }

      // Debounce the refetch to batch multiple calls together
      refetchSwimlaneCostsTimeoutRef.current = setTimeout(() => {
        const { budgetId: providedBudgetId } = options;
        // Use provided budgetId or fall back to selectedBudget from context
        const budgetId = providedBudgetId || selectedBudget?.budget;

        // Invalidate swimlane queries so the parent swimlane list updates its cost.
        // The detail sheet is protected from stale overwrites by dirty row tracking
        // (dirtyRowsRef) and stale response rejection (lastAppliedDataUpdatedAtRef).
        queryClient.invalidateQueries({
          queryKey: ['get-swimlanes-in-budget'],
          exact: false,
        });

        // Invalidate detail sheet totals (fetched independently from row data)
        queryClient.invalidateQueries({
          queryKey: ['detail-sheet-totals'],
          exact: false,
        });

        // Invalidate contractual charges and budget-by-id so any active subscriber gets fresh data
        if (budgetId) {
          queryClient.invalidateQueries({
            queryKey: ['get-contractual-charges', budgetId],
            exact: false,
          });
          queryClient.invalidateQueries({
            queryKey: ['get-film-incentives', budgetId],
            exact: false,
          });
          queryClient.invalidateQueries({
            queryKey: ['get-budget-by-id', { budgetId }],
          });
        }

        refetchSwimlaneCostsTimeoutRef.current = null;
      }, 200); // Debounce delay to batch multiple calls
    },
    [queryClient, selectedBudget]
  );

  /**
   * Sync project total from backend budgetData.total_cost
   * The backend is the single source of truth for budget totals
   */
  const recalculateProjectTotal = useCallback(() => {
    // Always use budgetData.total_cost from backend as the source of truth
    if (budgetData?.total_cost !== undefined) {
      setProjectTotal(parseFloat(budgetData.total_cost) || 0);
    } else {
      setProjectTotal(0);
    }
  }, [budgetData, setProjectTotal]);

  const navigateToFringes = () => {
    navigate(`${ALL_ROUTES.HOME_SCREEN_CHILDREN.FRINGES_DETAILS}?type=budget`);
  };

  const navigateToGlobals = () => {
    navigate(`${ALL_ROUTES.HOME_SCREEN_CHILDREN.GLOBALS_DETAILS}?type=budget`);
  };

  const navigateToTags = () => {
    navigate(`${ALL_ROUTES.HOME_SCREEN_CHILDREN.TAGS_DETAILS}?type=budget`);
  };

  const navigateToCredits = () => {
    navigate(`${ALL_ROUTES.HOME_SCREEN_CHILDREN.CREDITS_DETAILS}?type=budget`);
  };

  const navigateToPrint = () => {
    navigate(
      `${ALL_ROUTES.HOME_SCREEN_CHILDREN.PRINT_BUDGET}?id=${selectedBudget?.budget}`
    );
  };
  const getBudgetTypeName = budget => {
    return getBudgetTemplateName(budget, budgetTypes);
  };
  const getProductionDetailsDefault = budget => {
    const templateName = getBudgetTypeName(budget).trim().toLowerCase();

    if (!templateName) return undefined;

    return PRINT_PRODUCTION_DETAILS_DEFAULT_TEMPLATE_NAMES.includes(
      templateName
    );
  };
  const exportToPdf = async () => {
    if (!selectedBudget?.budget) {
      toast.error('No budget selected. Please select a budget first.');
      return;
    }

    if (budgetDownloadMutation.isLoading) {
      toast.info('PDF generation is already in progress...');
      return;
    }

    try {
      toast.info('Preparing PDF...');
      const productionDetailsDefault =
        getProductionDetailsDefault(selectedBudget);
      const payload = {
        budget_id: selectedBudget.budget,
        include_comments: true,
        include_fringe_details: true,
        include_zero_value_categories: true,
        include_zero_value_line_items: true,
        paper_size: getInitialPrintPaperSize(),
        timezone: getLocalTimezone(),
      };
      if (productionDetailsDefault !== undefined) {
        payload.include_production_details = productionDetailsDefault;
      }

      const response = await budgetDownloadMutation.mutateAsync(payload);
      const fileName = createTimestampedPdfFileName(
        selectedBudget?.name || selectedBudget?.full_name || 'Budget'
      );

      downloadFile(response.data, {
        contentDispositionHeader: response.headers?.['content-disposition'],
        contentTypeHeader:
          response.headers?.['content-type'] || 'application/pdf',
        fileName,
      });
      toast.dismiss();
      toast.success('PDF generated successfully!');
    } catch (error) {
      const message =
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        error.message;
      toast.dismiss();
      toast.error(
        message || 'An error occurred while downloading the budget',
        toastStyle
      );
    }
  };
  const exportToCsv = async () => {
    if (!selectedBudget?.budget) {
      toast.error('No budget selected. Please select a budget first.');
      return;
    }

    try {
      toast.info('Preparing CSV...');
      const [
        budgetResponse,
        contractualChargesResponse,
        filmIncentivesResponse,
      ] = await Promise.all([
        API_ENDPOINTS.budgets.get_budget_data(selectedBudget.budget),
        API_ENDPOINTS.budgets.get_contractual_charges(selectedBudget.budget),
        API_ENDPOINTS.budgets.get_film_incentives(selectedBudget.budget),
      ]);
      const contractualCharges = Array.isArray(contractualChargesResponse)
        ? contractualChargesResponse
        : contractualChargesResponse?.data || [];
      const filmIncentives = Array.isArray(filmIncentivesResponse)
        ? filmIncentivesResponse
        : filmIncentivesResponse?.data || [];

      downloadBudgetCsv({
        budgetData: budgetResponse?.data,
        selectedBudget,
        contractualCharges,
        filmIncentives,
      });
    } catch (error) {
      const message =
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        error?.message;

      toast.error(
        message || 'An error occurred while exporting the budget CSV.'
      );
    }
  };
  const exportToJson = async () => {
    if (!selectedBudget?.budget) {
      toast.error('No budget selected. Please select a budget first.');
      return;
    }

    try {
      toast.info('Preparing JSON...');
      const response = await API_ENDPOINTS.budgets.download_budget_json({
        budget_id: selectedBudget.budget,
      });

      downloadFile(response.data, {
        contentDispositionHeader: response.headers?.['content-disposition'],
        contentTypeHeader:
          response.headers?.['content-type'] || 'application/json',
        fileName: `${
          selectedBudget?.name || selectedBudget?.full_name || 'Budget'
        }-budget.json`,
      });
      toast.dismiss();
      toast.success('JSON exported successfully!');
    } catch (error) {
      const message =
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        error?.message;

      toast.dismiss();
      toast.error(
        message || 'An error occurred while exporting the budget JSON.'
      );
    }
  };
  const getBudgetType = () => {
    return getBudgetTypeName(selectedBudget);
  };

  const editMenuHandlersRef = useRef({});
  const registerEditMenuHandlers = useCallback(handlers => {
    editMenuHandlersRef.current = handlers || {};

    return () => {
      if (editMenuHandlersRef.current === handlers) {
        editMenuHandlersRef.current = {};
      }
    };
  }, []);

  const runEditMenuAction = useCallback(action => {
    const handler = editMenuHandlersRef.current?.[action];
    if (typeof handler === 'function') {
      handler();
    }
  }, []);

  const selectedBudgetPreferenceId = selectedBudget?.budget ?? null;
  const selectedBudgetIsAicp = isAicpTemplateName(
    getBudgetTypeName(selectedBudget)
  );

  useEffect(() => {
    const storageKey = getAccountNumbersViewStorageKey(
      selectedBudgetPreferenceId
    );
    const savedPreference =
      typeof window !== 'undefined' && storageKey
        ? window.localStorage?.getItem(storageKey)
        : null;
    const storedPreference =
      savedPreference === null ? selectedBudgetIsAicp : savedPreference === '1';

    setAccountNumbersViewPreference(prevPreference => {
      if (
        prevPreference.budgetId === selectedBudgetPreferenceId &&
        prevPreference.value === storedPreference
      ) {
        return prevPreference;
      }

      return {
        budgetId: selectedBudgetPreferenceId,
        value: storedPreference,
      };
    });
  }, [selectedBudgetIsAicp, selectedBudgetPreferenceId]);

  useEffect(() => {
    const storageKey = getAccountNumbersViewStorageKey(
      selectedBudgetPreferenceId
    );
    if (
      typeof window === 'undefined' ||
      !storageKey ||
      accountNumbersViewPreference.budgetId !== selectedBudgetPreferenceId
    ) {
      return;
    }

    window.localStorage?.setItem(
      storageKey,
      accountNumbersViewPreference.value ? '1' : '0'
    );
  }, [accountNumbersViewPreference, selectedBudgetPreferenceId]);

  useEffect(() => {
    const storageKey = getBudgetSidePanelStorageKey(selectedBudgetPreferenceId);
    const savedPreference =
      typeof window !== 'undefined' && storageKey
        ? window.localStorage?.getItem(storageKey)
        : null;
    const storedPreference = savedPreference === '1';

    setAiInsightsViewPreference(current => {
      if (
        current.budgetId === selectedBudgetPreferenceId &&
        current.value === storedPreference
      ) {
        return current;
      }
      return {
        budgetId: selectedBudgetPreferenceId,
        value: storedPreference,
      };
    });
  }, [selectedBudgetPreferenceId]);

  useEffect(() => {
    const storageKey = getBudgetSidePanelStorageKey(selectedBudgetPreferenceId);
    if (
      typeof window === 'undefined' ||
      !storageKey ||
      aiInsightsViewPreference.budgetId !== selectedBudgetPreferenceId
    ) {
      return;
    }

    window.localStorage?.setItem(
      storageKey,
      aiInsightsViewPreference.value ? '1' : '0'
    );
  }, [aiInsightsViewPreference, selectedBudgetPreferenceId]);

  const toggleAccountNumbers = useCallback(() => {
    setShowAccountNumbers(prev => !prev);
  }, [setShowAccountNumbers]);
  const toggleAiInsights = useCallback(() => {
    setShowAiInsights(prev => !prev);
  }, [setShowAiInsights]);

  const menuActions = {
    cut: () => runEditMenuAction('cut'),
    copy: () => runEditMenuAction('copy'),
    paste: () => runEditMenuAction('paste'),
    'add-comment': () => runEditMenuAction('add-comment'),
    'duplicate-budget': duplicateBudget,
    'duplicate-budget-with-values': duplicateBudgetWithValues,
    'new-budget': showCreateBudgetModal,
    'import-script': showScriptImportModal,
    'import-budget': showBudgetImportModal,
    'new-folder': showCreateFolderModal,
    'delete-budget': showDeleteBudgetModal,
    'delete-multi-budget': showDeleteMultiBudgetModal,
    'delete-folder': showDeleteFolderModal,
    'rename-budget': setBudgetToEdit,
    'share-budget': showShareBudgetModal,
    'manage-collaborators': showShareBudgetModal,
    'email-share-link': showShareBudgetModal,
    'budget-settings': showBudgetSettingsModal,
    'save-as-template': showSaveAsTemplateModal,
    'cost-report': showCostReportModal,
    'print-budget': navigateToPrint,
    'export-to-pdf': navigateToPrint,
    'budget-fringes': navigateToFringes,
    'budget-globals': navigateToGlobals,
    'budget-tags': navigateToTags,
    'budget-credits': navigateToCredits,
    'toggle-account-numbers': toggleAccountNumbers,
    'hide-show-insights': toggleAiInsights,
    'export-pdf': exportToPdf,
    'export-csv': exportToCsv,
    'export-json': exportToJson,
  };

  const menuPermissions = useMemo(
    () => ({
      'duplicate-budget': isBudgetOwner() && selectedBudget?.budget,
      'duplicate-budget-with-values': isBudgetOwner() && selectedBudget?.budget,
      'delete-budget': isBudgetOwner() && selectedBudget?.budget,
      'rename-budget': isBudgetOwner() && selectedBudget?.budget,
      'share-budget': selectedBudget?.budget,
      'archive-budget': isBudgetOwner() && selectedBudget?.budget,
      'manage-collaborators': isBudgetOwner() && selectedBudget?.budget,
      'email-share-link': isBudgetOwner() && selectedBudget?.budget,
      'budget-settings': selectedBudget?.budget,
      'save-as-template': isBudgetOwner() && selectedBudget?.budget,
      'budget-fringes': selectedBudget?.budget,
      'budget-globals': selectedBudget?.budget,
      'budget-tags': selectedBudget?.budget,
      'budget-credits': selectedBudget?.budget,
      'toggle-account-numbers': selectedBudget?.budget,
      'hide-show-insights': selectedBudget?.budget,
      'print-budget': selectedBudget?.budget,
      'cost-report': selectedBudget?.budget,
      'export-csv': selectedBudget?.budget,
      'export-json': selectedBudget?.budget,
      cut: selectedBudget?.budget && hasEditAccess(),
      copy: selectedBudget?.budget,
      paste: selectedBudget?.budget && hasEditAccess(),
      'add-comment': selectedBudget?.budget && showDetailedView?.show,
    }),
    [hasEditAccess, isBudgetOwner, selectedBudget, showDetailedView?.show]
  );

  return (
    <>
      <homeScreenContext.Provider
        value={{
          createTabsMutation,
          setMutationCompleted,
          sefFringeAppliedTotal,
          mutationCompleted,
          fringeApplied,
          makeAciveTabMutation,
          reorderTabsMutation,
          closeTabsMutation,
          createBudgetMutation,
          updateFrindeMutation, //fringe
          UpdateGroupsMutation,
          moveBudgetMutation,
          moveBudgetFolderMutation,
          createFolderMutation,
          renameFolderMutation,
          duplicateBudgetMutation,
          deleteBudgetMutation,
          deleteFringeMutation, // fringe
          deleteGroupMutation,
          createSwimlaneMutation,
          addSubCategoryMutation,
          updateSwimlaneMutation,
          deleteSwimlaneMutation,
          showCreateGroupsModal,
          setshowCreateGroupsModal,
          createSwimlaneDetailsMutation,
          createDuplicateCategory,
          createDuplicateCategoryLineItem,
          updateSwimlaneDetailsMutation,
          deleteSwimlaneDetailsMutation,
          shareBudgetMutation,
          updatePublicBudgetLinkMutation,
          removeBudgetAccessMutation,
          removeMultiBudgetsMutuation,
          removeFolderMutuation,
          budgetDownloadMutation,

          getBudgetsStatus,
          budgetList,
          fringeList, //fringe
          isFetchingBudgets,
          isErrorBudgets,
          refetchBudgets,
          refetchFringe, //fringe
          refetchGroup,
          getTabsStatus,
          openTabsListResponse,
          isFetchingTabs,
          isErrorTabs,
          refetchTabs,
          getFolderType,
          foldersData,
          isFetchingFolders,
          isErrorFoldersTypes,
          refetchFolders,
          getBudgetIdStatus,
          budgetData,
          isFetchingBudgetId,
          isErrorBudgetId,
          refetchBudgetId,
          refetchFringeId,

          selectedBudget,
          setSelectedBudget,
          currentBudgetViewState,
          setCurrentBudgetViewState,
          deepLinkViewStateByBudget,
          clearDeepLinkViewState,

          getBudgetTypeStatus,
          budgetTypes,
          isFetchingBudgetTypes,
          isErrorBudgetTypes,
          refetchBudgetTypes,

          getContextualSuggestionsStatus,
          contextualSuggestions,
          isFetchingContextualSuggestions,
          isErrorContextualSuggestions,
          refetchContextualSuggestions,

          getCollaboratorsStatus,
          collaboratorsList,
          isFetchingCollaborators,
          isErrorCollaborators,
          refetchCollaborators,

          getBudgetPermissionsStatus,
          budgetPermissions,
          isFetchingBudgetPermissions,
          isErrorBudgetPermissions,
          refetchBudgetPermissions,

          showDetailedView,
          setShowDetailedView,
          activeSwimlane,
          setActiveSwimlane,
          swimlanes,
          setSwimlanes,
          projectTotal,
          setProjectTotal,
          setOpenTabsList,
          setOpenTabsListInitial,
          budgetListData,
          openTabsList,
          openTabsListInitial,
          folderListing,
          getbudgetDropdownData,
          showCreateBudget,
          showScriptImport,
          showBudgetImport,
          showEditBudget,
          setShowEditBudget,
          showCreateFolder,
          showDeleteBudget,
          setActiveBudget,
          setShowColumnFringe,
          showAccountNumbers,
          setShowAccountNumbers,
          toggleAccountNumbers,
          showAiInsights,
          setShowAiInsights,
          toggleAiInsights,
          activeBudget,
          showcolumnfringe,
          showDeleteFringe, // fringe
          showEditFringeGroup,
          showDeleteFringeGroup,
          showDeleteGroup,
          showDeleteMultiBudgets,
          showDeleteFolder,
          renameBudget,
          duplicateBudget,
          duplicateBudgetWithValues,
          hideCreateBudgetModal,
          hideEditBudgetModal,
          hideUpdateFringeModal, //fringe
          hideUpdateGroupModal,
          showCreateBudgetModal,
          showScriptImportModal,
          hideScriptImportModal,
          showBudgetImportModal,
          hideBudgetImportModal,
          showEditBudgetModal,
          showUpdateFringeModal, //fringe
          showUpdateGroupModal,
          hideCreateFolderModal,
          showCreateFolderModal,
          hideDeleteBudgetModal,
          showDeleteBudgetModal,
          hideDeleteMultiBudgetModal,
          hideDeleteFringeModal, //fringe
          hideEditFringeGroupModal,
          hideDeleteFringeGroupModal,
          hideDeleteGroupModal,
          showDeleteMultiBudgetModal,
          showDeleteFolderModal,
          hideDeleteFolderModal,
          setBudgetToRename,
          clearBudgetRename,
          showShareBudgetModal,
          hideShareBudgetModal,
          showBudgetSettings,
          showBudgetSettingsModal,
          hideBudgetSettingsModal,
          showSaveAsTemplate,
          showSaveAsTemplateModal,
          hideSaveAsTemplateModal,
          showCostReport,
          showCostReportModal,
          hideCostReportModal,
          showLogOutModal,
          showNewBudgetId,
          printShow,
          setPrintShow,
          keyStore,
          setKeyStore,
          tabActive,
          setTabActive,
          setNewBudgetIdFunc,
          hideLogOutModal,
          logOutModalShow,
          showShareBudget,
          addedContextualSuggestions,
          setAddedContextualSuggestions,
          menuActions,
          menuPermissions,
          registerEditMenuHandlers,
          handleCreateTabs,
          openCreatedBudget,
          enteringBudgetId,
          openingCreatedBudgetId,
          optimisticallyRemoveBudget,
          changeTab,
          selectTab,
          renameTab,
          handleTabDragEnd,
          selectTabFromDropdown,
          refetchSwimlaneCosts,
          recalculateProjectTotal,
          isBudgetOwner,
          hasEditAccess,
          hasBudgetEditAccessInList,
          getBudgetType,
          updateSwimlaneItem,
          delData,
          folderData,
          setSelectedDeldata,
          setSeletedDelFringe, //fringe
          setSelectFringeGroup,
          setSelectDelFringeGroup,
          setSelectDelGroup,
          setFolderSelectedDeldata,
          setWindowReload,
          frindgeDetailsForModal,
          setFringeDetailsForModal,
          groupDetailsForModal,
          setGroupDetailsForModal,
        }}
      >
        {children}
      </homeScreenContext.Provider>
    </>
  );
};

HomeScreenContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useHomeScreenContext = () => {
  const {
    delData,
    folderData,
    setWindowReload,
    setSelectedDeldata,
    setSeletedDelFringe, //fringe
    setSelectFringeGroup,
    setSelectDelFringeGroup,
    setSelectDelGroup,
    setFolderSelectedDeldata,
    createTabsMutation,
    setMutationCompleted,
    sefFringeAppliedTotal,
    mutationCompleted,
    fringeApplied,
    makeAciveTabMutation,
    reorderTabsMutation,
    closeTabsMutation,
    createBudgetMutation,
    updateFrindeMutation, //fringe
    UpdateGroupsMutation,
    moveBudgetMutation,
    moveBudgetFolderMutation,
    createFolderMutation,
    renameFolderMutation,
    duplicateBudgetMutation,
    deleteBudgetMutation,
    deleteFringeMutation, // fringe
    deleteGroupMutation,
    createSwimlaneMutation,
    addSubCategoryMutation,
    updateSwimlaneMutation,
    deleteSwimlaneMutation,
    showCreateGroupsModal,
    setshowCreateGroupsModal,
    createSwimlaneDetailsMutation,
    createDuplicateCategory,
    createDuplicateCategoryLineItem,
    updateSwimlaneDetailsMutation,
    deleteSwimlaneDetailsMutation,
    shareBudgetMutation,
    updatePublicBudgetLinkMutation,
    removeBudgetAccessMutation,
    removeMultiBudgetsMutuation,
    removeFolderMutuation,
    budgetDownloadMutation,

    getBudgetsStatus,
    budgetList,
    fringeList, //fringe
    isFetchingBudgets,
    isErrorBudgets,
    refetchBudgets,
    refetchFringe, //fringe
    refetchGroup,
    getTabsStatus,
    openTabsListResponse,
    isFetchingTabs,
    isErrorTabs,
    refetchTabs,
    getFolderType,
    foldersData,
    isFetchingFolders,
    isErrorFoldersTypes,
    refetchFolders,
    getBudgetIdStatus,
    budgetData,
    isFetchingBudgetId,
    isErrorBudgetId,
    refetchBudgetId,
    refetchFringeId,

    selectedBudget,
    setSelectedBudget,
    currentBudgetViewState,
    setCurrentBudgetViewState,
    deepLinkViewStateByBudget,
    clearDeepLinkViewState,

    getBudgetTypeStatus,
    budgetTypes,
    isFetchingBudgetTypes,
    isErrorBudgetTypes,
    refetchBudgetTypes,

    getContextualSuggestionsStatus,
    contextualSuggestions,
    isFetchingContextualSuggestions,
    isErrorContextualSuggestions,
    refetchContextualSuggestions,

    getCollaboratorsStatus,
    collaboratorsList,
    isFetchingCollaborators,
    isErrorCollaborators,
    refetchCollaborators,

    getBudgetPermissionsStatus,
    budgetPermissions,
    isFetchingBudgetPermissions,
    isErrorBudgetPermissions,
    refetchBudgetPermissions,

    showDetailedView,
    setShowDetailedView,
    activeSwimlane,
    setActiveSwimlane,
    swimlanes,
    setSwimlanes,
    projectTotal,
    setProjectTotal,
    setOpenTabsList,
    setOpenTabsListInitial,
    budgetListData,
    openTabsList,
    openTabsListInitial,
    folderListing,
    getbudgetDropdownData,
    showCreateBudget,
    showScriptImport,
    showBudgetImport,
    showEditBudget,
    setShowEditBudget,
    showCreateFolder,
    showDeleteBudget,
    setActiveBudget,
    setShowColumnFringe,
    showAccountNumbers,
    setShowAccountNumbers,
    toggleAccountNumbers,
    showAiInsights,
    setShowAiInsights,
    toggleAiInsights,
    activeBudget,
    showcolumnfringe,
    showDeleteFringe, // fringe
    showEditFringeGroup,
    showDeleteFringeGroup,
    showDeleteGroup,
    showDeleteMultiBudgets,
    showDeleteFolder,
    renameBudget,
    duplicateBudget,
    duplicateBudgetWithValues,
    hideCreateBudgetModal,
    hideEditBudgetModal,
    hideUpdateFringeModal, //fringe
    hideUpdateGroupModal,
    showCreateBudgetModal,
    showScriptImportModal,
    hideScriptImportModal,
    showBudgetImportModal,
    hideBudgetImportModal,
    showEditBudgetModal,
    showUpdateFringeModal, //fringe
    showUpdateGroupModal,
    hideCreateFolderModal,
    showCreateFolderModal,
    hideDeleteBudgetModal,
    showDeleteBudgetModal,
    hideDeleteMultiBudgetModal,
    hideDeleteFringeModal, // fringe
    hideEditFringeGroupModal,
    hideDeleteFringeGroupModal,
    hideDeleteGroupModal,
    showDeleteMultiBudgetModal,
    showDeleteFolderModal,
    hideDeleteFolderModal,
    setBudgetToRename,
    clearBudgetRename,
    showShareBudgetModal,
    hideShareBudgetModal,
    showShareBudget,
    showBudgetSettings,
    showBudgetSettingsModal,
    hideBudgetSettingsModal,
    showSaveAsTemplate,
    showSaveAsTemplateModal,
    hideSaveAsTemplateModal,
    showCostReport,
    showCostReportModal,
    hideCostReportModal,
    showLogOutModal,
    showNewBudgetId,
    printShow,
    setPrintShow,
    keyStore,
    setKeyStore,
    tabActive,
    setTabActive,
    setNewBudgetIdFunc,
    hideLogOutModal,
    logOutModalShow,
    addedContextualSuggestions,
    setAddedContextualSuggestions,
    menuActions,
    menuPermissions,
    registerEditMenuHandlers,
    handleCreateTabs,
    openCreatedBudget,
    enteringBudgetId,
    openingCreatedBudgetId,
    optimisticallyRemoveBudget,
    changeTab,
    selectTab,
    renameTab,
    handleTabDragEnd,
    selectTabFromDropdown,
    refetchSwimlaneCosts,
    recalculateProjectTotal,
    isBudgetOwner,
    hasEditAccess,
    hasBudgetEditAccessInList,
    getBudgetType,
    updateSwimlaneItem,
    frindgeDetailsForModal,
    setFringeDetailsForModal,
    groupDetailsForModal,
    setGroupDetailsForModal,
  } = useContext(homeScreenContext);

  return {
    delData,
    folderData,
    setWindowReload,
    setSelectedDeldata,
    setSeletedDelFringe, //fringe
    setSelectFringeGroup,
    setSelectDelFringeGroup,
    setSelectDelGroup,
    setFolderSelectedDeldata,
    createTabsMutation,
    setMutationCompleted,
    sefFringeAppliedTotal,
    mutationCompleted,
    fringeApplied,
    makeAciveTabMutation,
    reorderTabsMutation,
    closeTabsMutation,
    createBudgetMutation,
    updateFrindeMutation, //fringe
    UpdateGroupsMutation,
    moveBudgetMutation,
    moveBudgetFolderMutation,
    createFolderMutation,
    renameFolderMutation,
    duplicateBudgetMutation,
    deleteBudgetMutation,
    deleteFringeMutation, // fringe
    deleteGroupMutation,
    createSwimlaneMutation,
    addSubCategoryMutation,
    updateSwimlaneMutation,
    deleteSwimlaneMutation,
    showCreateGroupsModal,
    setshowCreateGroupsModal,
    createSwimlaneDetailsMutation,
    createDuplicateCategory,
    createDuplicateCategoryLineItem,
    updateSwimlaneDetailsMutation,
    deleteSwimlaneDetailsMutation,
    shareBudgetMutation,
    updatePublicBudgetLinkMutation,
    removeBudgetAccessMutation,
    removeMultiBudgetsMutuation,
    removeFolderMutuation,
    budgetDownloadMutation,

    getBudgetsStatus,
    budgetList,
    fringeList, //fringe
    isFetchingBudgets,
    isErrorBudgets,
    refetchBudgets,
    refetchFringe, //fringe
    refetchGroup,
    getTabsStatus,
    openTabsListResponse,
    isFetchingTabs,
    isErrorTabs,
    refetchTabs,
    getFolderType,
    foldersData,
    isFetchingFolders,
    isErrorFoldersTypes,
    refetchFolders,
    getBudgetIdStatus,
    budgetData,
    isFetchingBudgetId,
    isErrorBudgetId,
    refetchBudgetId,
    refetchFringeId,
    selectedBudget,
    setSelectedBudget,
    currentBudgetViewState,
    setCurrentBudgetViewState,
    deepLinkViewStateByBudget,
    clearDeepLinkViewState,

    getBudgetTypeStatus,
    budgetTypes,
    isFetchingBudgetTypes,
    isErrorBudgetTypes,
    refetchBudgetTypes,

    getContextualSuggestionsStatus,
    contextualSuggestions,
    isFetchingContextualSuggestions,
    isErrorContextualSuggestions,
    refetchContextualSuggestions,

    getCollaboratorsStatus,
    collaboratorsList,
    isFetchingCollaborators,
    isErrorCollaborators,
    refetchCollaborators,

    getBudgetPermissionsStatus,
    budgetPermissions,
    isFetchingBudgetPermissions,
    isErrorBudgetPermissions,
    refetchBudgetPermissions,

    showDetailedView,
    setShowDetailedView,
    activeSwimlane,
    setActiveSwimlane,
    swimlanes,
    setSwimlanes,
    projectTotal,
    setProjectTotal,
    setOpenTabsList,
    setOpenTabsListInitial,
    budgetListData,
    openTabsList,
    openTabsListInitial,
    folderListing,
    getbudgetDropdownData,
    showCreateBudget,
    showScriptImport,
    showBudgetImport,
    showEditBudget,
    setShowEditBudget,
    showCreateFolder,
    showDeleteBudget,
    setActiveBudget,
    setShowColumnFringe,
    showAccountNumbers,
    setShowAccountNumbers,
    toggleAccountNumbers,
    showAiInsights,
    setShowAiInsights,
    toggleAiInsights,
    activeBudget,
    showcolumnfringe,
    showDeleteFringe, // fringe
    showEditFringeGroup,
    showDeleteFringeGroup,
    showDeleteGroup,
    showDeleteMultiBudgets,
    showDeleteFolder,
    renameBudget,
    duplicateBudget,
    duplicateBudgetWithValues,
    hideCreateBudgetModal,
    hideEditBudgetModal,
    hideUpdateFringeModal, //fringe
    hideUpdateGroupModal,
    showCreateBudgetModal,
    showScriptImportModal,
    hideScriptImportModal,
    showBudgetImportModal,
    hideBudgetImportModal,
    showEditBudgetModal,
    showUpdateFringeModal, //fringe
    showUpdateGroupModal,
    hideCreateFolderModal,
    showCreateFolderModal,
    hideDeleteBudgetModal,
    showDeleteBudgetModal,
    hideDeleteMultiBudgetModal,
    hideDeleteFringeModal, // fringe
    hideEditFringeGroupModal,
    hideDeleteFringeGroupModal,
    hideDeleteGroupModal,
    showDeleteMultiBudgetModal,
    showDeleteFolderModal,
    hideDeleteFolderModal,
    setBudgetToRename,
    clearBudgetRename,
    showShareBudgetModal,
    hideShareBudgetModal,
    showShareBudget,
    showBudgetSettings,
    showBudgetSettingsModal,
    hideBudgetSettingsModal,
    showSaveAsTemplate,
    showSaveAsTemplateModal,
    hideSaveAsTemplateModal,
    showCostReport,
    showCostReportModal,
    hideCostReportModal,
    showLogOutModal,
    showNewBudgetId,
    printShow,
    setPrintShow,
    keyStore,
    setKeyStore,
    tabActive,
    setTabActive,
    setNewBudgetIdFunc,
    hideLogOutModal,
    logOutModalShow,
    addedContextualSuggestions,
    setAddedContextualSuggestions,
    menuActions,
    menuPermissions,
    registerEditMenuHandlers,
    handleCreateTabs,
    openCreatedBudget,
    enteringBudgetId,
    openingCreatedBudgetId,
    optimisticallyRemoveBudget,
    changeTab,
    selectTab,
    renameTab,
    handleTabDragEnd,
    selectTabFromDropdown,
    refetchSwimlaneCosts,
    recalculateProjectTotal,
    isBudgetOwner,
    hasEditAccess,
    hasBudgetEditAccessInList,
    getBudgetType,
    updateSwimlaneItem,
    frindgeDetailsForModal,
    setFringeDetailsForModal,
    groupDetailsForModal,
    setGroupDetailsForModal,
  };
};
